// src/admin/Bookings.jsx

import React, { useState, useEffect } from "react";

import {
  Table,
  Tag,
  Button,
  Modal,
  Descriptions,
  Space,
  Input,
  Select,
  message,
} from "antd";

import {
  SearchOutlined,
  EyeOutlined,
  QrcodeOutlined,
} from "@ant-design/icons";

import axios from "axios";


const STATUS_COLOR = {
  PENDING:   "orange",
  CONFIRMED: "blue",
  COMPLETED: "green",
  CANCELLED: "red",
};


const Bookings = () => {

  const [data,        setData]        = useState([]);
  const [searchText,  setSearchText]  = useState("");
  const [viewBooking, setViewBooking] = useState(null);
  const [qrBooking,   setQrBooking]   = useState(null);


  // =========================
  // FETCH BOOKINGS
  // =========================

  useEffect(() => {
    fetchBookings();
  }, []);


  const fetchBookings = async () => {
    try {
      const res = await axios.get(
        "https://prashantbha.pythonanywhere.com/api/bookings/"
      );

      console.log("BOOKINGS:", res.data);

      const formatted = res.data.map((b) => ({
        key:            b.id,
        user:           b.full_name,
        phone:          b.mobile,

        // TRIP
        trip_type:      b.trip_type,
        departure_date: new Date(b.departure_date).toLocaleDateString("en-IN"),
        return_date:    b.return_date
          ? new Date(b.return_date).toLocaleDateString("en-IN")
          : null,

        // VIA CITIES — array from API, e.g. ["Rajkot", "Ahmedabad"]
        via_cities: Array.isArray(b.via_cities)
          ? b.via_cities.filter(Boolean)
          : (b.via_cities || "")
              .split(",")
              .map(c => c.trim())
              .filter(Boolean),

        pickup:             b.pickup_address,
        dropoff:            b.drop_address,
        route:              `${b.pickup_address} → ${b.drop_address}`,
        cab:                b.cab_name,
        driver:             "Assigned Later",
        date:               new Date(b.created_at).toLocaleDateString("en-IN"),
        time:               b.pickup_time || "-",
        price:              Number(b.total_fare || 0),
        status:             b.status,
        addons:             [],
        couponApplied:      false,

        base_fare:          b.base_fare,
        addon_total:        b.addon_total,
        other_charges:      b.other_charges,
        trip_distance:      b.trip_distance,
        eta_minutes:        b.eta_minutes,
        driver_language:    b.driver_language,
        refundable_booking: b.refundable_booking,
        pet_allowed:        b.pet_allowed,
        luggage_assistance: b.luggage_assistance,
      }));

      setData(formatted);

    } catch (err) {
      console.error("BOOKING FETCH ERROR:", err);
    }
  };


  // =========================
  // SEARCH FILTER
  // =========================

  const filtered = data.filter(
    (b) =>
      b.user.toLowerCase().includes(searchText.toLowerCase()) ||
      b.route.toLowerCase().includes(searchText.toLowerCase()) ||
      b.status.toLowerCase().includes(searchText.toLowerCase()) ||
      b.via_cities.join(" ").toLowerCase().includes(searchText.toLowerCase())
  );


  // =========================
  // UPDATE STATUS
  // =========================

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      await axios.patch(
        `https://prashantbha.pythonanywhere.com/api/bookings/${bookingId}/status/`,
        { status: newStatus }
      );
      message.success("Booking status updated");
      setData((prev) =>
        prev.map((item) =>
          item.key === bookingId ? { ...item, status: newStatus } : item
        )
      );
    } catch (err) {
      console.error(err);
      message.error("Failed to update status");
    }
  };


  // =========================
  // TABLE COLUMNS
  // =========================

  const columns = [

    {
      title: "#",
      key: "idx",
      render: (_, __, i) => (
        <span style={{ color:"#9ca3af", fontWeight:600 }}>{i + 1}</span>
      ),
      width: 40,
    },

    {
      title: "Passenger",
      key: "user",
      render: (r) => (
        <div>
          <div style={{ fontWeight:700, color:"#1a1a2e" }}>{r.user}</div>
          <div style={{ fontSize:12, color:"#9ca3af" }}>📞 {r.phone}</div>
        </div>
      ),
    },

    {
      title: "Route",
      key: "route",
      render: (r) => (
        <div>
          {/* Trip type badge */}
          <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
            <Tag
              color={r.trip_type === "ROUNDTRIP" ? "purple" : "blue"}
              style={{ fontSize:10, margin:0 }}
            >
              {r.trip_type === "ROUNDTRIP" ? "↩ Round" : "→ One Way"}
            </Tag>
          </div>

          {/* Pickup → Drop */}
          <div style={{ fontWeight:600, fontSize:13, marginTop:4 }}>{r.route}</div>

          {/* Via cities */}
          {r.via_cities.length > 0 && (
            <div style={{ marginTop:4, display:"flex", alignItems:"center", gap:4, flexWrap:"wrap" }}>
              <span style={{ fontSize:11, color:"#9ca3af", fontWeight:600 }}>via</span>
              {r.via_cities.map((city, i) => (
                <Tag
                  key={i}
                  color="gold"
                  style={{ fontSize:10, margin:0, borderRadius:50, padding:"1px 7px" }}
                >
                  📍 {city}
                </Tag>
              ))}
            </div>
          )}

          <div style={{ fontSize:11, color:"#9ca3af", marginTop:3 }}>
            {r.date} · {r.time}
          </div>
        </div>
      ),
    },

    {
      title: "Cab",
      dataIndex: "cab",
      key: "cab",
    },

    {
      title: "Fare",
      dataIndex: "price",
      key: "price",
      render: (p) => (
        <b style={{ color:"#d4a80f" }}>₹{p.toLocaleString()}</b>
      ),
    },

    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status, record) => (
        <Select
          value={status}
          onChange={(value) => updateBookingStatus(record.key, value)}
          style={{ width:150 }}
          options={[
            { label:"Pending",   value:"PENDING"   },
            { label:"Confirmed", value:"CONFIRMED" },
            { label:"Completed", value:"COMPLETED" },
            { label:"Cancelled", value:"CANCELLED" },
          ]}
          dropdownStyle={{ borderRadius:12, padding:6 }}
        />
      ),
    },

    {
      title: "Actions",
      key: "actions",
      render: (record) => (
        <Space wrap size={4}>
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => setViewBooking(record)}
          >
            View
          </Button>
          <Button
            size="small"
            icon={<QrcodeOutlined />}
            onClick={() => setQrBooking(record)}
          >
            QR
          </Button>
        </Space>
      ),
    },
  ];


  // =========================
  // FAKE QR
  // =========================

  const FakeQR = ({ text }) => {
    const size  = 10;
    const cells = Array.from({ length: size * size }, () => Math.random() > 0.45);
    return (
      <div style={{ display:"inline-block", padding:12, background:"#fff", borderRadius:12, boxShadow:"0 4px 16px rgba(0,0,0,.1)" }}>
        <div style={{ display:"grid", gridTemplateColumns:`repeat(${size},1fr)`, gap:2 }}>
          {cells.map((on, i) => (
            <div key={i} style={{ width:18, height:18, background:on?"#1a1a2e":"#fff", borderRadius:2 }} />
          ))}
        </div>
        <div style={{ textAlign:"center", fontSize:11, color:"#9ca3af", marginTop:8, fontFamily:"monospace" }}>
          {text}
        </div>
      </div>
    );
  };


  return (

    <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif" }}>

      {/* HEADER */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20, flexWrap:"wrap", gap:12 }}>
        <div>
          <h2 style={{ fontSize:22, fontWeight:800, color:"#1a1a2e", margin:0 }}>Bookings</h2>
          <p style={{ color:"#9ca3af", fontSize:13, marginTop:2 }}>{data.length} Total Bookings</p>
        </div>
        <Input
          prefix={<SearchOutlined style={{ color:"#9ca3af" }} />}
          placeholder="Search booking..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width:280, borderRadius:8 }}
          allowClear
        />
      </div>

      {/* TABLE */}
      <Table
        dataSource={filtered}
        columns={columns}
        rowKey="key"
        scroll={{ x:true }}
        pagination={{ pageSize:8 }}
        style={{ background:"#fff", borderRadius:16, overflow:"hidden" }}
      />

      {/* VIEW MODAL */}
      <Modal
        title="📋 Booking Details"
        open={!!viewBooking}
        onCancel={() => setViewBooking(null)}
        footer={null}
        width={580}
      >
        {viewBooking && (
          <Descriptions column={1} bordered size="small">

            <Descriptions.Item label="Passenger">
              {viewBooking.user}
            </Descriptions.Item>

            <Descriptions.Item label="Phone">
              {viewBooking.phone}
            </Descriptions.Item>

            <Descriptions.Item label="Trip Type">
              <Tag color={viewBooking.trip_type === "ROUNDTRIP" ? "purple" : "blue"}>
                {viewBooking.trip_type === "ROUNDTRIP" ? "↩ Round Trip" : "→ One Way"}
              </Tag>
            </Descriptions.Item>

            <Descriptions.Item label="Pickup">
              {viewBooking.pickup}
            </Descriptions.Item>

            {/* Via Cities */}
            {viewBooking.via_cities.length > 0 && (
              <Descriptions.Item label="Via Cities">
                <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                  {viewBooking.via_cities.map((city, i) => (
                    <Tag
                      key={i}
                      color="gold"
                      style={{ borderRadius:50, padding:"2px 10px", fontSize:12 }}
                    >
                      📍 {city}
                    </Tag>
                  ))}
                </div>
                {/* Route preview string */}
                <div style={{ marginTop:6, fontSize:12, color:"#6b7280" }}>
                  {viewBooking.pickup.split(",")[0]}
                  {viewBooking.via_cities.map(c => ` → ${c.split(",")[0]}`).join("")}
                  {" → "}{viewBooking.dropoff.split(",")[0]}
                </div>
              </Descriptions.Item>
            )}

            <Descriptions.Item label="Drop">
              {viewBooking.dropoff}
            </Descriptions.Item>

            <Descriptions.Item label="Departure Date">
              {viewBooking.departure_date}
            </Descriptions.Item>

            {viewBooking.trip_type === "ROUNDTRIP" && (
              <Descriptions.Item label="Return Date">
                {viewBooking.return_date || "-"}
              </Descriptions.Item>
            )}

            <Descriptions.Item label="Pickup Time">
              {viewBooking.time || "-"}
            </Descriptions.Item>

            <Descriptions.Item label="Cab">
              {viewBooking.cab}
            </Descriptions.Item>

            <Descriptions.Item label="Trip Distance">
              {viewBooking.trip_distance} KM
            </Descriptions.Item>

            <Descriptions.Item label="ETA">
              {viewBooking.eta_minutes} mins
            </Descriptions.Item>

            <Descriptions.Item label="Base Fare">
              ₹{viewBooking.base_fare}
            </Descriptions.Item>

            <Descriptions.Item label="Addon Total">
              ₹{viewBooking.addon_total}
            </Descriptions.Item>

            <Descriptions.Item label="Final Fare">
              ₹{viewBooking.price}
            </Descriptions.Item>

            <Descriptions.Item label="Luggage Assistance">
              {viewBooking.luggage_assistance ? "Yes" : "No"}
            </Descriptions.Item>

            <Descriptions.Item label="Pet Allowed">
              {viewBooking.pet_allowed ? "Yes" : "No"}
            </Descriptions.Item>

            <Descriptions.Item label="Driver Language">
              {viewBooking.driver_language || "Not Selected"}
            </Descriptions.Item>

            <Descriptions.Item label="Status">
              {viewBooking.status}
            </Descriptions.Item>

          </Descriptions>
        )}
      </Modal>

      {/* QR MODAL */}
      <Modal
        title="📱 Booking QR"
        open={!!qrBooking}
        onCancel={() => setQrBooking(null)}
        footer={null}
      >
        {qrBooking && (
          <div style={{ textAlign:"center" }}>
            <FakeQR text={`BK-${qrBooking.key}`} />
          </div>
        )}
      </Modal>

    </div>
  );
};

export default Bookings;